import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { getSeason, bindSeasonSlot } from "../pages/fgl/season-api.js";
import { loadRoundSnapshot } from "../api/round-api.js";
import { roundJournal } from "../data/local-database.js";
import { SeasonRoundStarter } from "../sync/season-round-start.js";
import {
  SeasonRoundStartStore,
  type PendingSeasonRound,
  type SeasonRoundSelection,
  type SeasonRoundSetupInput,
} from "../sync/season-round-start-store.js";
import type { ActionContext } from "./round-workspace-sync.js";
import type { RoundSnapshot } from "../../domain/round/round-types.js";

export interface SeasonWorkspaceActions {
  pendingSeason: PendingSeasonRound | null;
  startSeasonRound(
    input: SeasonRoundSetupInput,
    selection: SeasonRoundSelection,
  ): Promise<boolean>;
  resumeSeasonRound(): Promise<boolean>;
  abandonSeasonRound(): Promise<boolean>;
}

export function useSeasonWorkspace(
  context: ActionContext,
  setBusy: Dispatch<SetStateAction<boolean>>,
): SeasonWorkspaceActions {
  const latest = useRef(context);
  latest.current = context;
  const store = useMemo(() => new SeasonRoundStartStore(localStorage), []);
  const [state, setState] = useState<{
    accountId: string;
    pending: PendingSeasonRound | null;
  }>({ accountId: context.accountId, pending: null });
  const starter = useMemo(
    () =>
      new SeasonRoundStarter({
        accountId: context.accountId,
        engine: context.engine,
        journal: roundJournal,
        store,
        api: { getSeason, bindSeasonSlot, loadRoundSnapshot },
        access: () => latest.current,
        nextRoundId: () => crypto.randomUUID(),
        changed: () => {
          if (latest.current.accountId === context.accountId)
            setState({
              accountId: context.accountId,
              pending: store.pending(context.accountId),
            });
        },
        withLock: (key, action) =>
          navigator.locks ? navigator.locks.request(key, action) : action(),
      }),
    [context.accountId, context.engine, store],
  );
  useEffect(() => {
    setBusy(false);
    try {
      setState({
        accountId: context.accountId,
        pending: store.pending(context.accountId),
      });
    } catch (error) {
      context.setMessage(message(error));
    }
  }, [context.accountId, context.setMessage, store, setBusy]);
  const run = (action: () => Promise<RoundSnapshot>) =>
    runSeasonAction(context, latest, setBusy, store, action);
  return {
    pendingSeason: state.accountId === context.accountId ? state.pending : null,
    startSeasonRound: (input, selection) =>
      run(() => starter.start(input, selection)),
    resumeSeasonRound: () => run(() => starter.resume()),
    abandonSeasonRound: () => run(() => starter.abandon()),
  };
}

async function runSeasonAction(
  context: ActionContext,
  latest: { current: ActionContext },
  setBusy: Dispatch<SetStateAction<boolean>>,
  store: SeasonRoundStartStore,
  action: () => Promise<RoundSnapshot>,
): Promise<boolean> {
  setBusy(true);
  context.setMessage("");
  try {
    if (context.conflicts.length)
      throw new Error("Resolve the score conflict before season setup.");
    await context.autoSync.settle();
    const snapshot = await action();
    if (latest.current.accountId !== context.accountId) return false;
    context.setSnapshot(snapshot);
    context.setMessage(
      snapshot.state === "abandoned"
        ? "Season round abandoned. Its reserved slot remains visible."
        : "Season round started. The agreed slot is saved.",
    );
    return true;
  } catch (error) {
    if (latest.current.accountId === context.accountId)
      context.setMessage(message(error));
    return false;
  } finally {
    try {
      await refreshSeasonState(context, latest, store);
    } catch (error) {
      if (latest.current.accountId === context.accountId)
        context.setMessage(message(error));
    } finally {
      if (latest.current.accountId === context.accountId) setBusy(false);
    }
  }
}

async function refreshSeasonState(
  context: ActionContext,
  latest: { current: ActionContext },
  store: SeasonRoundStartStore,
): Promise<void> {
  if (latest.current.accountId !== context.accountId) return;
  const intent = store.read(context.accountId);
  const [snapshot, queued] = await Promise.all([
    intent
      ? roundJournal.getSnapshot(context.accountId, intent.roundId)
      : undefined,
    roundJournal.hasUnsynced(context.accountId),
  ]);
  if (latest.current.accountId !== context.accountId) return;
  if (snapshot) context.setSnapshot(snapshot);
  context.setQueued(queued);
  const engineConflicts = context.engine.getConflicts();
  const conflicts = engineConflicts.length
    ? engineConflicts
    : context.conflicts;
  context.setConflicts(conflicts);
  context.setConflicted(conflicts.length > 0);
}

function message(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "The saved season setup could not continue.";
}
